import Alert from 'react-bootstrap/Alert';
import React, { Component, ChangeEvent, ReactNode, FormEvent } from 'react';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Form from 'react-bootstrap/Form';
import './LoginForm.scss';
import { AuthHelper, AuthToken, AuthChallenge } from '../../helpers/AuthHelper';


interface LoginFormProps {
  onLogin: (session: AuthToken|AuthChallenge) => void;
}

interface LoginFormState {
  username: string;
  password: string;
  usernameError?: string;
  passwordError?: string;
  loginError?: string;
  loading?: boolean;
}

export class LoginForm extends Component<LoginFormProps, LoginFormState> {
  private static readonly INPUT_USERNAME = 'username';
  private static readonly INPUT_PASSWORD = 'password';

  constructor(props: LoginFormProps) {
    super(props);
    this.state = {
      username: '',
      password: ''
    };
  }

  handleInputChange = (event: ChangeEvent): void => {
    const { name, value } = (event.target as HTMLInputElement);
    switch (name) {
      case LoginForm.INPUT_USERNAME:
        this.setState({ username: value });
        break;
      case LoginForm.INPUT_PASSWORD:
        this.setState({ password: value });
        break;
    }
  };

  resetErrors = (): void => {
    this.setState({
      usernameError: undefined,
      passwordError: undefined,
      loginError: undefined,
    });
  };

  validate = (username: string, password: string): boolean => {
    let valid = true;
    if (!username || username.length === 0) {
      this.setState({ usernameError: 'Vul je gebruikersnaam in' });
      valid = false;
    }
    if (!password || password.length === 0) {
      this.setState({ passwordError: 'Vul je wachtwoord in' });
      valid = false;
    }
    return valid;
  };

  handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    this.resetErrors();

    const { username, password } = this.state;
    const isValid = this.validate(username, password);
    if (!isValid) {
      return;
    }

    this.setLoading();
    AuthHelper.login(username, password)
      .then(session => {
        this.props.onLogin(session);
      })
      .catch(err => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        switch (err.code) {
          case 'NotAuthorizedException':
            this.setState({ loginError: 'De opgegeven gebruikersnaam en wachtwoord combinatie is niet geldig' });
            break;
          case 'UserNotFoundException':
            this.setState({ loginError: 'Voor de opgegeven gebruikersnaam bestaat geen account' });
            break;
          case 'LimitExceededException':
            this.setState({ loginError: 'Er zijn te veel pogingen gedaan, probeer het later opnieuw' });
            break;
          default:
            this.setState({ loginError: 'Inloggen is mislukt door een onverwachte fout' });
            console.error('Unexpected auth response:', err);
            break;
        }
      })
      .finally(() => this.setLoadingDone());
  };

  private setLoading = () => this.setState({ loading: true });
  private setLoadingDone = () => this.setState({ loading: false });

  render(): ReactNode {
    const { usernameError, passwordError, loginError, loading } = this.state;
    return (
      <>
        <Form className="form" onSubmit={this.handleSubmit}>
          <Alert variant="info">
            MijnTOPP bevat vertrouwelijke persoonlijke informatie. Ga zorgvuldig om met deze gegevens,
            zie geen gegevens in die niet voor jou bedoeld zijn en geef geen anderen toegang tot gegevens
            die niet voor hen bedoeld zijn.
          </Alert>

          <Form.Group className="form__item">
            <Form.Label>Gebruikersnaam</Form.Label>
            <Form.Control
              name={LoginForm.INPUT_USERNAME}
              type="text"
              autoComplete="username"
              onChange={this.handleInputChange}
              isInvalid={!!usernameError}
            />
            {usernameError && <Alert variant="danger">{usernameError}</Alert> }
          </Form.Group>

          <Form.Group className="form__item">
            <Form.Label>Wachtwoord</Form.Label>
            <Form.Control
              name={LoginForm.INPUT_PASSWORD}
              type="password"
              autoComplete="current-password"
              onChange={this.handleInputChange}
              isInvalid={!!passwordError}
            />
            {passwordError && <Alert variant="danger">{passwordError}</Alert> }
          </Form.Group>

          {loginError && <Alert variant="danger">{loginError}</Alert> }
          <Button className="form__submit" variant="primary" type="submit" disabled={loading}>
            {
              loading &&
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            }
            Inloggen
          </Button>
        </Form>
      </>
    );
  }
}
